// A screen that throws while drawing, caught (scenario O7, B-270).
//
// Without this a render error anywhere unmounts the whole tree and leaves a
// blank page with no way on: no rail, no tabs, nothing to tap. Inside the
// shell it wraps the screen alone, so the navigation around it still works
// and the person can go somewhere else; at the root it wraps the app, for a
// screen drawn outside the shell — the front door, joining with a code.
//
// It REPORTS the crash (`reportError('crash', …)`), with the component stack
// React hands over, and says so only once the site has taken the report.
//
// `resetKey` is the path: moving to another screen clears the crash, so going
// somewhere else is never also stuck on it. "Try again" draws the same screen
// afresh, which is what a crash on a half-arrived answer needs.

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Line, Stack, Text, Title } from '../kit/ui';
import { reportError } from '../data/errorReports';

interface Props {
  resetKey: string;
  children: ReactNode;
}

interface State {
  failed: boolean;
  reported: boolean;
  /** The `resetKey` the crash happened under. */
  at: string;
}

export class ScreenBoundary extends Component<Props, State> {
  state: State = { failed: false, reported: false, at: '' };

  static getDerivedStateFromError(): Partial<State> {
    return { failed: true };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    // Somewhere else now: the crash, and whether it was reported, belonged
    // to the screen that was left.
    if (state.at !== '' && state.at !== props.resetKey) {
      return { failed: false, reported: false, at: '' };
    }
    return null;
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    const message = error instanceof Error ? error.message : String(error);
    const stack = [error instanceof Error ? error.stack : null, info.componentStack]
      .filter((s): s is string => typeof s === 'string' && s !== '')
      .join('\n--- component stack ---\n');
    const at = this.props.resetKey;
    this.setState({ at });
    // SAID ONLY ONCE THE SITE TOOK IT: a report refused, unanswered or never
    // sent (a repeat, the page's ten) leaves the line off. Once taken it stays
    // said — the same crash after "Try again" is the same report — until the
    // person moves to another screen, which the check of `at` stands for.
    void reportError('crash', message, { stack, error }).then((taken) => {
      if (taken) this.setState((state) => (state.at === at ? { reported: true } : null));
    });
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="fsp-crash" role="alert">
        <Stack gap={3}>
          <Title>This screen tripped over something</Title>
          <Text block tone="secondary">
            Something on it broke while it was drawing. The rest of the site still works: try it
            again, or head to another screen.
          </Text>
          {this.state.reported ? (
            <Text block tone="muted" size="xs">
              The site has been sent the details, so there is nothing you need to describe.
            </Text>
          ) : null}
          <Line gap={2}>
            <Button variant="primary" onClick={() => this.setState({ failed: false })}>
              Try again
            </Button>
            <Button variant="quiet" onClick={() => window.location.reload()}>
              Reload the page
            </Button>
          </Line>
        </Stack>
      </div>
    );
  }
}

/** The boundary, cleared by moving to another path. */
export function RouteBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <ScreenBoundary resetKey={pathname}>{children}</ScreenBoundary>;
}
